import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/components/StatusBadge";
import { CalendarRange } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthsAgoISO = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

export default function Analytics() {
  const [dateFrom, setDateFrom] = useState(monthsAgoISO(6));
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    const { data } = await api.get(`/api/dashboard/monthly?${params}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight">Business Analytics</h1>
        <p className="text-sm text-slate-500 mt-1">Monthly sales, receipts and BD performance for a date range.</p>
      </div>

      <Card className="border-slate-200 shadow-none p-4">
        <div className="flex flex-wrap items-end gap-3">
          <CalendarRange className="w-5 h-5 text-slate-400 mb-2" />
          <div className="space-y-1"><Label className="text-xs">From</Label><Input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
          <Button className="bg-brand hover:bg-brand-hover" onClick={load} data-testid="apply-range">Apply</Button>
          <div className="ml-auto flex gap-1 text-xs">
            {[
              ["1M", 1], ["3M", 3], ["6M", 6], ["12M", 12],
            ].map(([label, n]) => (
              <button key={label} onClick={() => { setDateFrom(monthsAgoISO(n)); setDateTo(todayISO()); setTimeout(load, 50); }} className="px-2 py-1 border border-slate-200 rounded hover:bg-slate-50">{label}</button>
            ))}
          </div>
        </div>
      </Card>

      {!data ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-slate-200 shadow-none p-4">
              <div className="font-heading font-medium mb-3">Monthly Sale <span className="text-xs text-slate-500">(new confirmed project value)</span></div>
              <table className="data-table w-full">
                <thead><tr><th>Month</th><th>By currency</th></tr></thead>
                <tbody>
                  {data.monthly_sales.map((m) => (
                    <tr key={m.month}>
                      <td className="font-medium">{m.month}</td>
                      <td>
                        {m.by_currency.map((c) => (
                          <span key={c.currency} className="mr-3">{formatCurrency(c.amount, c.currency)}</span>
                        ))}
                      </td>
                    </tr>
                  ))}
                  {data.monthly_sales.length === 0 && <tr><td colSpan={2} className="text-center text-slate-500 py-4">No sales in this range.</td></tr>}
                </tbody>
              </table>
            </Card>

            <Card className="border-slate-200 shadow-none p-4">
              <div className="font-heading font-medium mb-3">Monthly Amount Received</div>
              <table className="data-table w-full">
                <thead><tr><th>Month</th><th>INR total</th><th>By currency</th></tr></thead>
                <tbody>
                  {data.monthly_received.map((m) => (
                    <tr key={m.month}>
                      <td className="font-medium">{m.month}</td>
                      <td className="text-emerald-700">{formatCurrency(m.inr_total, "INR")}</td>
                      <td className="text-slate-600">
                        {m.by_currency.map((c) => (
                          <span key={c.currency} className="mr-3 text-xs">{formatCurrency(c.amount, c.currency)}</span>
                        ))}
                      </td>
                    </tr>
                  ))}
                  {data.monthly_received.length === 0 && <tr><td colSpan={3} className="text-center text-slate-500 py-4">No payments in this range.</td></tr>}
                </tbody>
              </table>
            </Card>
          </div>

          <Card className="border-slate-200 shadow-none p-4">
            <div className="font-heading font-medium mb-3">BD Business Done <span className="text-xs text-slate-500">(confirmed project value assigned to each BD)</span></div>
            <table className="data-table w-full">
              <thead><tr><th>BD</th><th>Projects</th><th>Value by currency</th></tr></thead>
              <tbody>
                {data.bd_business.map((b) => (
                  <tr key={b.bd_user_id}>
                    <td className="font-medium">{b.bd_name || "(unnamed)"}</td>
                    <td>{b.projects_count}</td>
                    <td>
                      {b.by_currency.map((c) => (
                        <span key={c.currency} className="mr-3">{formatCurrency(c.amount, c.currency)}</span>
                      ))}
                    </td>
                  </tr>
                ))}
                {data.bd_business.length === 0 && <tr><td colSpan={3} className="text-center text-slate-500 py-4">No BD activity in this range.</td></tr>}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
